import { useListAllAlarmsByHabitatQuery } from '@akin-home/alarms/react';
import { useListAllAlertsByHabitatQuery } from '@akin-home/alerts/react';
import {
  AlarmByHabitatQueryVariables,
  AlertByHabitatQueryVariables,
  CalendarItemType,
  EventByHabitatQueryVariables,
  ModelStringKeyConditionInput,
} from '@akin-home/amplify';
import { sortByStartDate } from '@akin-home/dates';
import { useListEventsByHabitatQuery } from '@akin-home/events/react';
import { useListMealsByHabitat } from '@akin-home/meals/react';
import { useMemo } from 'react';
import { CalendarItemUnion } from '../types';

export type useListCalendarItemsQueryParams = AlertByHabitatQueryVariables &
  AlarmByHabitatQueryVariables &
  EventByHabitatQueryVariables;

type Filter = { from?: string; to?: string };

interface Params {
  types?: CalendarItemType[];
  params: { habitatID: string; filter?: Filter };
}

export const useListCalendarItems = (params: Params) => {
  const { types } = params;

  const { data: alerts, isLoading: loadingAlerts } =
    useListAllAlertsByHabitatQuery(params.params);

  const { data: alarms, isLoading: loadingAlarms } =
    useListAllAlarmsByHabitatQuery(params.params);

  const { data: events, isLoading: loadingEvents } =
    useListEventsByHabitatQuery(params.params);

  const { data: meals, isLoading: loadingMeals } = useListMealsByHabitat(
    {
      habitatID: params.params.habitatID,
      start: getStartFilter(params.params.filter),
    },
    {
      enabled: !types || types.includes(CalendarItemType.Meal),
    }
  );

  /** Combine and sort together */
  const items = useMemo(
    () =>
      [alerts || [], alarms || [], events || [], meals || []]
        .flat()
        .sort(sortByStartDate) as CalendarItemUnion[],
    [alerts, alarms, events, meals]
  );

  return {
    items,
    isLoading: loadingAlerts || loadingAlarms || loadingEvents || loadingMeals,
  };
};

const getStartFilter = (
  filter?: Filter
): ModelStringKeyConditionInput | undefined => {
  if (!filter) return;

  if (filter.from && filter.to) {
    return { between: [filter.from, filter.to] };
  } else if (filter.from) {
    return { ge: filter.from };
  } else if (filter.to) {
    return { le: filter.to };
  }

  return;
};
